import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * A responsive image gallery for feed posts, comments and replies. Accepts a list
 * of image URLs (1..N) and lays them out Facebook-style — a single image shows at
 * its natural size; multiples pack into a grid with a "+N" tile on overflow — and
 * opens a full-screen lightbox (keyboard + arrow nav, click backdrop to close) on
 * tap. `size` tunes the thumbnail heights: "sm" for comments/replies, "md" for
 * posts.
 */
export default function PostImages({ images = [], size = "md", className = "" }) {
  const list = (images || []).filter(Boolean);
  const [lightbox, setLightbox] = useState(-1); // index open in the lightbox, or -1
  if (list.length === 0) return null;

  const open = (i) => setLightbox(i);
  const h = size === "sm" ? "h-40" : "h-60";
  const hHalf = size === "sm" ? "h-[4.75rem]" : "h-[7.25rem]";

  const Img = ({ src, className: c, onClick, children }) => (
    <button type="button" onClick={onClick} className={`group relative block w-full overflow-hidden bg-gray-100 ${c}`}>
      <img src={src} alt="" className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]" />
      {children}
    </button>
  );

  let grid;
  if (list.length === 1) {
    // Single image — show it at a natural, contained height (no crop).
    grid = (
      <button type="button" onClick={() => open(0)} className="block overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
        <img src={list[0]} alt="" className={`w-full ${size === "sm" ? "max-h-72" : "max-h-[30rem]"} object-contain`} />
      </button>
    );
  } else if (list.length === 2) {
    grid = (
      <div className="grid grid-cols-2 gap-1 overflow-hidden rounded-xl">
        {list.map((src, i) => <Img key={i} src={src} className={h} onClick={() => open(i)} />)}
      </div>
    );
  } else if (list.length === 3) {
    grid = (
      <div className="grid grid-cols-2 gap-1 overflow-hidden rounded-xl">
        <Img src={list[0]} className={`row-span-2 ${h} !h-full`} onClick={() => open(0)} />
        <Img src={list[1]} className={hHalf} onClick={() => open(1)} />
        <Img src={list[2]} className={hHalf} onClick={() => open(2)} />
      </div>
    );
  } else {
    // 4+ → a 2×2 grid; the 4th tile carries a "+N" overlay for the remainder.
    const extra = list.length - 4;
    grid = (
      <div className="grid grid-cols-2 gap-1 overflow-hidden rounded-xl">
        {list.slice(0, 4).map((src, i) => (
          <Img key={i} src={src} className={h} onClick={() => open(i)}>
            {i === 3 && extra > 0 && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-2xl font-extrabold text-white">
                +{extra}
              </span>
            )}
          </Img>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      {grid}
      {lightbox >= 0 && (
        <Lightbox images={list} index={lightbox} onClose={() => setLightbox(-1)} onIndex={setLightbox} />
      )}
    </div>
  );
}

/* Full-screen viewer — arrow keys / on-screen chevrons / Esc, counter, backdrop close. */
function Lightbox({ images, index, onClose, onIndex }) {
  const count = images.length;
  const step = useCallback((d) => onIndex((index + d + count) % count), [index, count, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, step]);

  const Chevron = ({ dir }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); step(dir === "left" ? -1 : 1); }}
      aria-label={dir === "left" ? "Previous" : "Next"}
      className="absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
      style={{ [dir]: "1rem" }}
    >
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
        <path strokeLinecap="round" strokeLinejoin="round" d={dir === "left" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
      </svg>
    </button>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4 animate-fade-in"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <img
        src={images[index]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
      />

      {count > 1 && (
        <>
          <Chevron dir="left" />
          <Chevron dir="right" />
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold text-white backdrop-blur">
            {index + 1} / {count}
          </span>
        </>
      )}
    </div>,
    document.body
  );
}
